import { useMemo, type ReactNode } from "react";
import { CommandItem } from "@/components/ui/command";
import { useListIntegrations, useListUsers } from "./components.js";
import type { ContextualAction } from "scantling-common";
import type { CmdkItemComponent } from "./cmdk_registry.js";

interface ActionCmdkItemProps {
  action: ContextualAction;
  onAction: (verb: string, params: Record<string, unknown>) => void;
  onClose: () => void;
  getIcon?: (action: ContextualAction) => ReactNode;
}

export function ActionCmdkItem({
  action,
  onAction,
  onClose,
  getIcon,
}: ActionCmdkItemProps) {
  const { argument } = action;

  // Each instance fetches only what it needs using the argument's query
  const integrationsResult = useListIntegrations(
    argument?.type === "integration" ? argument.query : undefined,
  );
  const usersResult = useListUsers(
    argument?.type === "user" ? argument.query : undefined,
  );

  const options = useMemo(() => {
    if (!argument) return [];

    if (argument.type === "integration") {
      return (integrationsResult.data?.entries ?? []).map(i => ({
        value: i.slug,
        label: i.niceName ?? i.slug,
      }));
    }
    if (argument.type === "user") {
      return (usersResult.data?.entries ?? []).map(u => ({
        value: u.id,
        label:
          u.firstName && u.lastName
            ? `${u.firstName} ${u.lastName}`
            : (u.email ?? u.id),
      }));
    }
    return [];
  }, [argument, integrationsResult.data, usersResult.data]);

  if (!argument) return null;

  const isLoading =
    (argument.type === "integration" && integrationsResult.isLoading) ||
    (argument.type === "user" && usersResult.isLoading);

  if (isLoading) {
    return (
      <CommandItem disabled value={`${action.verb}-loading`}>
        {getIcon?.(action)}
        {action.label}...
      </CommandItem>
    );
  }

  if (options.length === 0) {
    return null;
  }

  return (
    <>
      {options.map(opt => (
        <CommandItem
          key={opt.value}
          value={`${action.verb}-${opt.value}`}
          onSelect={() => {
            onAction(action.verb, {
              ...action.params,
              [argument.key]: opt.value,
            });
            onClose();
          }}
        >
          {getIcon?.(action)}
          {action.label} → {opt.label}
        </CommandItem>
      ))}
    </>
  );
}

/** Factory to create an action cmdk item component */
export function createActionCmdkItem(
  action: ContextualAction,
  onAction: (verb: string, params: Record<string, unknown>) => void,
  getIcon?: (action: ContextualAction) => ReactNode,
): CmdkItemComponent {
  return function ActionCmdkItemWrapper({ onClose }) {
    return (
      <ActionCmdkItem
        action={action}
        onAction={onAction}
        onClose={onClose}
        getIcon={getIcon}
      />
    );
  };
}
